import { API } from "aws-amplify";
import { convertFromRaw, EditorState } from "draft-js";
import ErrorPage from "next/error";
import Router, { useRouter } from "next/router";
import { useEffect, useState } from "react";
import styled from "styled-components";
import Swal from "sweetalert2";
import CompanyHeader from "../../../../shared/components/companies/CompanyHeader";
import { CompanyDescriptionRead } from "../../../../shared/components/companies/profile-read/CompanyDescriptionRead";
import AlertManager from "../../../../shared/components/global/AlertManager";
import Device from "../../../../shared/components/global/Device/Device";
import StyledLink from "../../../../shared/components/global/StyledLink";
import JobCard from "../../../../shared/components/job/JobCard";
import { JobDescriptionRead } from "../../../../shared/components/jobs/job/JobDescriptionRead";
import JobLink from "../../../../shared/components/jobs/job/JobLink";
import JobsHeader from "../../../../shared/components/jobs/job/JobsHeader";
import { Job } from "../../../../shared/model/Job";
import { GridTileContainer } from "../../../../shared/styles/backend/layout";
import { Button, ButtonSizes } from "../../../../shared/styles/button";
import {
  Container,
  ContainerContentFull,
  ContainerContentStandard,
} from "../../../../shared/styles/container";
import Layout from "../../components/global/Layout";
import Seo from "../../components/global/Seo";
import JobsHeroHeader from "../../components/jobs/JobsHeroHeader";
import JobsSearchbar from "../../components/jobs/JobsSearchbar";
import { listJobs } from "../../graphql/queries";
import { BUCKET_URL } from "../../model/S3Bucket";
import { executeFetchJobs } from "../../services/JobService";

const CompanyProfileArticle = styled.article`
  background-color: ${(props) => props.theme.colors.lightBg};
`;

const ContainerContentFullHeader = styled.div`
  grid-column: full;
  position: absolute;
  height: 100%;
  width: 100%;
`;
interface SearchbarOuterContainerProps {
  mobileMargin: boolean;
}
const SearchbarOuterContainer = styled.div<SearchbarOuterContainerProps>`
  position: relative;
  height: 100%;
  width: 100%;
  margin-top: ${(props) => (props.mobileMargin ? "12px" : "0")};
`;
const JobCardListWrapper = styled.div`
  margin-top: 128px;
  margin-bottom: 128px;
`;
const JobCardList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  .job-card {
    margin-bottom: 16px;
  }
`;
const MoreJobsContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

interface JobPageProps {
  slug: string;
  initialJob: Job;
}

export default function JobPage({ slug, initialJob }: JobPageProps) {
  const router = useRouter();

  const [similarJobs, setSimilarJobs] = useState<Job[]>([]);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [job, setJob] = useState<Job>(initialJob);

  useEffect(() => {
    const fetchSimilarJobs = async (jobResult: Job) => {
      const similarJobsUnfiltered: Job[] = await executeFetchJobs(
        getVariablesForSimilarJobs(jobResult)
      );
      // a job should not have itself as a similar job.
      const similarJobsFiltered = similarJobsUnfiltered.filter(
        (item: Job) => item.id != jobResult?.id
      );
      setSimilarJobs(similarJobsFiltered);
    };
    const fetchJob = async () => {
      const allJobs = await getAllJobsRecursivePagination();
      const jobResult = allJobs.find((job) => job.slug === slug);
      setJob(jobResult);
      if (jobResult.expiresAt != null && jobResult.expiresAt?.length > 0) {
        if (new Date(jobResult.expiresAt) < new Date()) {
          setIsExpired(true);
          Swal.fire(AlertManager.JobExpired()).then(() => {
            Router.push("/jobs");
          });
        } else {
          fetchSimilarJobs(jobResult);
        }
      }
    };
    fetchJob();
  }, [slug, initialJob]);

  const getVariablesForSimilarJobs = (jobResult: Job) => {
    const newOr: any = [];
    newOr.push({ category: { matchPhrase: jobResult.category } });
    newOr.push({ companyJobsId: { matchPhrase: jobResult.companyJobsId } });
    const variables = {
      limit: 6,
      filter: {
        or: newOr,
      },
    };
    return variables;
  };

  if (!router.isFallback && job == null) {
    return <ErrorPage statusCode={404} />;
  }

  const getDescriptionRaw = () => {
    try {
      const raw = EditorState.createWithContent(
        convertFromRaw(JSON.parse(job.description))
      );
      const description = raw.getCurrentContent().getPlainText("\u0001");
      if (description != null && description?.length > 0) {
        return description.substring(0, 152) + "...";
      }
    } catch (error) {
      console.error(error);
    }
    return "";
  };

  return (
    <Layout preview={false}>
      {router.isFallback || job == null ? (
        <div></div>
      ) : (
        <>
          <Seo
            title={job.title}
            description={getDescriptionRaw()}
            imageSrc={BUCKET_URL + job.company?.bannerUrl}
          />
          <Device>
            {({ isMobile }) =>
              isMobile ? (
                <Container backgroundColor="#FAFAFA" height="auto">
                  <ContainerContentStandard>
                    <SearchbarOuterContainer mobileMargin>
                      <JobsSearchbar top={"0px"} />
                    </SearchbarOuterContainer>
                  </ContainerContentStandard>
                </Container>
              ) : (
                <Container backgroundColor="#FAFAFA" height="120px">
                  <ContainerContentFullHeader>
                    <JobsHeroHeader small />
                  </ContainerContentFullHeader>
                  <ContainerContentStandard>
                    <SearchbarOuterContainer mobileMargin={false}>
                      <JobsSearchbar top={"25%"} />
                    </SearchbarOuterContainer>
                  </ContainerContentStandard>
                </Container>
              )
            }
          </Device>

          <Container>
            <ContainerContentStandard>
              <JobsHeader job={job} bucketURL={BUCKET_URL} />
            </ContainerContentStandard>
            <ContainerContentFull>
              <CompanyProfileArticle>
                <Container>
                  <ContainerContentStandard>
                    <GridTileContainer>
                      <JobDescriptionRead job={job} />
                      {!isExpired && <JobLink job={job} />}
                    </GridTileContainer>
                  </ContainerContentStandard>

                  <ContainerContentFull>
                    <CompanyHeader
                      title={job.company?.name}
                      srcURL={job.company?.bannerUrl}
                      bucketUrl={BUCKET_URL}
                    />
                  </ContainerContentFull>

                  <ContainerContentStandard>
                    <GridTileContainer>
                      <CompanyDescriptionRead company={job.company} />
                      {!isExpired && <JobLink job={job} />}

                      {similarJobs != null && similarJobs?.length > 0 && (
                        <JobCardListWrapper>
                          <h1>Ähnliche Jobs</h1>
                          <JobCardList>
                            {similarJobs?.map((job, index) => {
                              return (
                                <StyledLink
                                  key={job?.title + index}
                                  href="/jobs/[slug]"
                                  asas={"/jobs/" + job.slug}
                                >
                                  <JobCard
                                    job={job}
                                    smallCard={false}
                                    bucketUrl={BUCKET_URL}
                                  />
                                </StyledLink>
                              );
                            })}
                          </JobCardList>
                          <MoreJobsContainer>
                            <StyledLink href="/jobs/search">
                              <Button primary size={ButtonSizes.m}>
                                Mehr Jobs entdecken
                              </Button>
                            </StyledLink>
                          </MoreJobsContainer>
                        </JobCardListWrapper>
                      )}
                    </GridTileContainer>
                  </ContainerContentStandard>
                </Container>
              </CompanyProfileArticle>
            </ContainerContentFull>
          </Container>
        </>
      )}
    </Layout>
  );
}

const getAllJobsRecursivePagination = (): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    let allJobs: Job[] = [];
    let nextToken = null;
    do {
      const allJobsResponse: any = await API.graphql({
        query: listJobs,
        variables: {
          limit: 100,
          nextToken: nextToken,
        },
        authMode: "API_KEY",
      });
      const jobs = allJobsResponse?.data?.listJobs?.items;
      nextToken = allJobsResponse?.data?.listJobs?.nextToken;
      allJobs = allJobs.concat(jobs);
    } while (nextToken != null);
    resolve(allJobs);
  });
};

export async function getStaticProps({ params }) {
  const allJobsResponse = await getAllJobsRecursivePagination();
  const jobResult = allJobsResponse.find((job) => job.slug === params.slug);

  return {
    props: {
      key: params.slug,
      slug: params.slug,
      initialJob: jobResult,
    },
    revalidate: 600,
  };
}

export async function getStaticPaths() {
  const allJobsResponse = await getAllJobsRecursivePagination();

  return {
    paths: allJobsResponse.map((job) => `/jobs/${job?.slug}`) || [],
    fallback: "blocking",
  };
}
